import React, { useMemo } from 'react';
import {
  formatKeySignature,
  formatTempo,
  formatTimeSignature,
  type KeySignature,
  type SongMeta,
} from '@chordbaker/music-theory';
import { useTheme, Theme } from '../contexts/ThemeContext';
import MusicKeyIcon from './icons/MusicKeyIcon';
import ClockIcon from './icons/ClockIcon';
import PlusIcon from './icons/PlusIcon';

type SongSetting = 'key' | 'time' | 'tempo';
const SETTINGS: SongSetting[] = ['key', 'time', 'tempo'];
const LABELS: Record<SongSetting, string> = { key: 'Key', time: 'Time', tempo: 'Tempo' };

const createStyles = (theme: Theme) => ({
  row: {
    display: 'flex',
    flexDirection: 'row' as const,
    flexWrap: 'wrap' as const,
    alignItems: 'center',
    gap: theme.spacing.sm,
    minHeight: theme.sizes.miniButton,
  },
  unsetGroup: {
    display: 'flex',
    flexDirection: 'row' as const,
    alignItems: 'center',
    gap: theme.spacing.sm,
    // WEB-SPECIFIC: CSS transition in place of RN's ControlsFade
    transition: `opacity ${theme.motion.duration.fast}ms ${theme.motion.easing.standard}`,
  },
  // Set value: the section repeat pill's outline, neutral text
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
    height: theme.sizes.miniButton,
    paddingLeft: theme.spacing.sm,
    paddingRight: theme.spacing.sm,
    border: `1px solid ${theme.colors.borderSecondary}`,
    borderRadius: theme.radii.full,
    backgroundColor: 'transparent',
    fontSize: theme.typography.sizes.sm,
    fontWeight: theme.typography.weights.semiBold,
    color: theme.colors.text,
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1,
    cursor: 'pointer',
  },
  chipIcon: {
    display: 'flex',
    color: theme.colors.textSecondary,
    width: theme.sizes.icon.sm,
    height: theme.sizes.icon.sm,
  },
  // Unset value: the dashed "add" language of the section and row controls
  addChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing.xxs,
    height: theme.sizes.miniButton,
    paddingLeft: theme.spacing.sm,
    paddingRight: theme.spacing.sm,
    border: `1px dashed ${theme.colors.border}`,
    borderRadius: theme.radii.full,
    backgroundColor: 'transparent',
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    lineHeight: 1,
    cursor: 'pointer',
  },
});

export interface SongMetaChipsProps {
  songKey: KeySignature | null;
  songMeta: SongMeta;
  /** Show the dashed chips for unset settings (something in the chart is active). */
  showUnset: boolean;
  onPressKey: () => void;
  onPressTime: () => void;
  onPressTempo: () => void;
}

/**
 * Song key, time signature and tempo as a row of chips under the title.
 * Set values are always shown, in that order; unset ones appear as dashed
 * "+" chips after them, only while something in the chart is active. The
 * row keeps its height either way, so the chart doesn't jump.
 */
const SongMetaChips: React.FC<SongMetaChipsProps> = ({
  songKey,
  songMeta,
  showUnset,
  onPressKey,
  onPressTime,
  onPressTempo,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);

  const values: Record<SongSetting, string | null> = {
    key: songKey ? formatKeySignature(songKey) : null,
    time: songMeta.timeSignature ? formatTimeSignature(songMeta.timeSignature) : null,
    tempo: songMeta.tempo !== null ? formatTempo(songMeta.tempo) : null,
  };
  const handlers: Record<SongSetting, () => void> = { key: onPressKey, time: onPressTime, tempo: onPressTempo };
  const titles: Record<SongSetting, string> = {
    key: `Song key ${values.key}`,
    time: `Time signature ${values.time}`,
    tempo: `Tempo ${songMeta.tempo} beats per minute`,
  };

  const set = SETTINGS.filter(setting => values[setting] !== null);
  const unset = SETTINGS.filter(setting => values[setting] === null);

  const icon = (setting: SongSetting) => {
    if (setting === 'key') {
      return <span style={styles.chipIcon}><MusicKeyIcon /></span>;
    }
    if (setting === 'time') {
      return <span style={styles.chipIcon}><ClockIcon size={theme.sizes.icon.sm} /></span>;
    }
    return null;
  };

  return (
    // song-meta-chips: the global click handler leaves the active state alone here
    <div style={styles.row} className="song-meta-chips" data-testid="song-meta-chips">
      {set.map(setting => (
        <button
          key={setting}
          type="button"
          onClick={handlers[setting]}
          style={styles.chip}
          aria-label={titles[setting]}
          title={titles[setting]}
          data-testid={`song-meta-${setting}`}
        >
          {icon(setting)}
          {values[setting]}
        </button>
      ))}

      {unset.length > 0 && (
        <div
          style={{
            ...styles.unsetGroup,
            opacity: showUnset ? 1 : 0,
            pointerEvents: showUnset ? 'auto' : 'none',
          }}
          aria-hidden={!showUnset}
        >
          {unset.map(setting => (
            <button
              key={setting}
              type="button"
              onClick={handlers[setting]}
              style={styles.addChip}
              tabIndex={showUnset ? 0 : -1}
              aria-label={`Add ${LABELS[setting].toLowerCase()}`}
              title={`Add ${LABELS[setting].toLowerCase()}`}
              data-testid={`song-meta-add-${setting}`}
            >
              <PlusIcon size={theme.sizes.icon.sm} />
              {LABELS[setting]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SongMetaChips;
